import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Search, Bell, User, LogOut, Sparkles } from 'lucide-react';
import { Input } from './ui/input';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import AIChatInterface from './AIChatInterface';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';

const Header = () => {
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [showResults, setShowResults] = useState(false);
  const [searchLoading, setSearchLoading] = useState(false);
  const [isChatOpen, setIsChatOpen] = useState(false);
  const [isChatMinimized, setIsChatMinimized] = useState(false);
  const { user, logout } = useAuth();

  const handleSearch = async (query) => {
    setSearchQuery(query);
    
    if (query.length < 2) {
      setSearchResults([]);
      setShowResults(false);
      return;
    }
    
    try {
      setSearchLoading(true);
      const backendUrl = process.env.REACT_APP_BACKEND_URL;
      
      // Search for assets across all categories + thematic equities universe
      const [cryptoRes, equityRes, commodityRes, stocksRes] = await Promise.all([
        axios.get(`${backendUrl}/api/assets/crypto`).catch(() => ({ data: { assets: [] } })),
        axios.get(`${backendUrl}/api/assets/equities`).catch(() => ({ data: { assets: [] } })),
        axios.get(`${backendUrl}/api/assets/commodities`).catch(() => ({ data: { assets: [] } })),
        axios.get(`${backendUrl}/api/stocks/search`, { params: { query } }).catch(() => ({ data: { results: [] } }))
      ]);
      
      const queryLower = query.toLowerCase();
      
      // Filter base assets
      const baseAssets = [
        ...(cryptoRes.data.assets || []).map(a => ({ ...a, category: 'crypto' })),
        ...(equityRes.data.assets || []).map(a => ({ ...a, category: 'equities' })),
        ...(commodityRes.data.assets || []).map(a => ({ ...a, category: 'commodities' }))
      ];
      
      const filteredBase = baseAssets.filter(asset => 
        asset.symbol?.toLowerCase().includes(queryLower) || 
        asset.name?.toLowerCase().includes(queryLower)
      );
      
      // Add thematic equities universe results (avoid duplicates)
      const baseSymbols = new Set(filteredBase.map(a => a.symbol?.toUpperCase()));
      const thematicResults = (stocksRes.data.results || [])
        .filter(s => !baseSymbols.has(s.ticker?.toUpperCase()))
        .map(s => ({
          symbol: s.ticker,
          name: s.name,
          category: 'equities',
          theme: s.theme
        }));
      
      const combined = [...filteredBase, ...thematicResults].slice(0, 10);
      
      setSearchResults(combined);
      setShowResults(combined.length > 0);
    } catch (error) {
      console.error('Search error:', error);
      setSearchResults([]);
    } finally {
      setSearchLoading(false);
    }
  };

  const handleResultClick = (asset) => {
    // Navigate to appropriate page with the symbol
    if (asset.category === 'crypto') {
      navigate(`/crypto/${asset.symbol?.toLowerCase()}`);
    } else if (asset.category === 'equities') {
      navigate(`/equities/${asset.symbol}`);
    } else if (asset.category === 'commodities') {
      navigate(`/commodities/${asset.symbol}`);
    }
    
    // Clear search
    setSearchQuery('');
    setShowResults(false);
    setSearchResults([]);
  };

  const handleChatToggle = () => {
    if (isChatOpen && isChatMinimized) {
      // If minimized, expand it
      setIsChatMinimized(false);
    } else if (isChatOpen && !isChatMinimized) {
      // If open and expanded, close it
      setIsChatOpen(false);
    } else {
      // If closed, open it
      setIsChatOpen(true);
      setIsChatMinimized(false);
    }
  };

  const handleChatClose = () => {
    setIsChatOpen(false);
    setIsChatMinimized(false);
  };

  const handleChatMinimize = () => {
    setIsChatMinimized(!isChatMinimized);
  };

  return (
    <>
      <header className="bg-white border-b border-gray-200 px-6 py-4 shadow-sm">
        <div className="flex items-center justify-between">
          {/* Left Section - Search Bar */}
          <div className="flex items-center space-x-4 flex-1">
            {/* Search Section with Results Dropdown */}
            <div className="relative w-96">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
              <Input
                type="text"
                placeholder="Search assets (stocks, crypto, commodities)..."
                value={searchQuery}
                onChange={(e) => handleSearch(e.target.value)}
                onFocus={() => searchResults.length > 0 && setShowResults(true)}
                onBlur={() => setTimeout(() => setShowResults(false), 200)}
                className="pl-10 bg-gray-50 border-gray-300 text-gray-900 placeholder-gray-500 focus:border-[#F7931A] focus:ring-[#F7931A]"
              />
              
              {/* Search Results Dropdown */}
              {showResults && searchResults.length > 0 && (
                <div className="absolute top-full left-0 right-0 mt-2 bg-white border border-gray-200 rounded-lg shadow-lg z-50 max-h-96 overflow-y-auto">
                  {searchResults.map((asset, index) => (
                    <div
                      key={asset.symbol || asset.id || index}
                      onClick={() => handleResultClick(asset)}
                      className="px-4 py-3 hover:bg-orange-50 cursor-pointer border-b border-gray-100 last:border-b-0"
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex items-center space-x-3">
                          {asset.image && (
                            <img src={asset.image} alt={asset.symbol} className="w-6 h-6 rounded-full" />
                          )}
                          <div>
                            <div className="font-semibold text-gray-900">{asset.symbol}</div>
                            <div className="text-xs text-gray-500">{asset.name}</div>
                            {asset.theme && <div className="text-xs text-orange-500">{asset.theme}</div>}
                          </div>
                        </div>
                        <Badge className={`
                          ${asset.category === 'crypto' ? 'bg-orange-100 text-orange-700' : ''}
                          ${asset.category === 'equities' ? 'bg-amber-100 text-amber-700' : ''}
                          ${asset.category === 'commodities' ? 'bg-yellow-100 text-yellow-700' : ''}
                        `}>
                          {asset.category}
                        </Badge>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Right Section */}
          <div className="flex items-center space-x-4">
            {/* User info and Logout */}
            <div className="flex items-center space-x-3">
              <Button
                size="md"
                onClick={() => navigate('/pricing')}
                className="bg-bitcoin-orange hover:bg-orange-700 text-white border-none shadow-lg px-6 py-2.5 font-bold text-base transition-all duration-200 transform hover:scale-105"
              >
                <Sparkles className="w-5 h-5 mr-2" />
                Upgrade
              </Button>
              <Badge variant="secondary" className="bg-orange-100 text-orange-700">
                <User className="w-3 h-3 mr-1" />
                {user?.username || 'Admin'}
              </Badge>
              <Button
                size="sm"
                variant="outline"
                onClick={logout}
                className="text-gray-600 hover:text-red-600 hover:border-red-300"
              >
                <LogOut className="w-4 h-4 mr-2" />
                Logout
              </Button>
            </div>
          </div>
        </div>
      </header>

      {/* AI Chat Interface - Temporarily Disabled 
      <AIChatInterface 
        isOpen={isChatOpen}
        onClose={handleChatClose}
        onMinimize={handleChatMinimize}
        isMinimized={isChatMinimized}
      />
      */}
    </>
  );
};

export default Header;